import React, { useEffect } from 'react'

interface NotificationToastProps {
  message: string
  type: 'success' | 'error'
  duration?: number
  onClose?: () => void
  className?: string
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  message,
  type,
  duration = type === 'success' ? 3000 : 5000,
  onClose,
  className = ''
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [duration, onClose])

  const baseClasses = 'fixed top-4 right-4 px-4 py-2 rounded shadow-lg z-50'
  const typeClasses = type === 'success'
    ? 'bg-green-500 text-white'
    : 'bg-red-500 text-white max-w-md'

  return (
    <div className={`${baseClasses} ${typeClasses} ${className}`}>
      {message}
    </div>
  )
}

// Hook for managing notification toasts
export const useNotificationToast = () => {
  const showNotification = (message: string, type: 'success' | 'error') => {
    const container = document.createElement('div')
    container.className = 'notification-toast-container'

    const handleClose = () => {
      if (document.body.contains(container)) {
        document.body.removeChild(container)
      }
    }

    const toastElement = (
      <NotificationToast
        message={message}
        type={type}
        onClose={handleClose}
      />
    )

    // Render the toast using React
    import('react-dom/client').then(({ createRoot }) => {
      const root = createRoot(container)
      root.render(toastElement)
      document.body.appendChild(container)
    })
  }

  return { showNotification }
}

// Utility functions for backward compatibility
export const showSuccessToast = (message: string) => {
  const successMessage = document.createElement('div')
  successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50'
  successMessage.textContent = message
  document.body.appendChild(successMessage)
  setTimeout(() => {
    if (document.body.contains(successMessage)) {
      document.body.removeChild(successMessage)
    }
  }, 3000)
}

export const showErrorToast = (message: string) => {
  const errorMessage = document.createElement('div')
  errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50 max-w-md'
  errorMessage.textContent = message
  document.body.appendChild(errorMessage)
  setTimeout(() => {
    if (document.body.contains(errorMessage)) {
      document.body.removeChild(errorMessage)
    }
  }, 5000)
}

export const showLearningToast = (jobId: string | null) => {
  if (!jobId) return

  // Lazy import to avoid circular dependencies
  import('@/lib/api').then(({ apiClient }) => {
    const container = document.createElement('div')
    container.className = 'fixed top-4 right-4 bg-slate-700 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3 transition-all duration-300'
    container.innerHTML = `
      <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>Applying correction...</span>
    `
    document.body.appendChild(container)

    let cancelled = false
    const startTime = Date.now()
    const pollInterval = setInterval(async () => {
      try {
        if (cancelled || Date.now() - startTime > 30000) {
          clearInterval(pollInterval)
          if (document.body.contains(container)) container.remove()
          return
        }

        const data = await apiClient.get<{ status: string }>(
          `/processing/queue-jobs/${jobId}/status`
        )

        if (data.status === 'completed' || data.status === 'finished') {
          clearInterval(pollInterval)
          container.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3 transition-all duration-300'
          container.innerHTML = `
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span>Pattern saved</span>
          `
          setTimeout(() => {
            if (document.body.contains(container)) container.remove()
          }, 3000)
        } else if (data.status === 'failed') {
          clearInterval(pollInterval)
          if (document.body.contains(container)) container.remove()
        }
      } catch {
        clearInterval(pollInterval)
        if (document.body.contains(container)) container.remove()
      }
    }, 2000)
  })
}
